import { useState, useEffect } from 'react';
import {
  getAllWorkoutsWithUsers,
  type FeedUser,
} from '../../infrastructure/repositories/leaderboardRepository';
import type { Workout } from '../../domain/workouts/types';

export interface AwardsData {
  workouts: Workout[];
  users: Record<string, FeedUser>;
}

/**
 * Awards are worked out from the whole log, and four different surfaces want
 * them - the feed, the leaderboard's trophy view, the trophy case on History,
 * and the case inside another lifter's modal. Sharing one in-flight promise
 * means visiting all four costs one read rather than four.
 *
 * Invalidated explicitly when a lift is logged, rather than expiring on a
 * timer: a stale trophy case is confusing in a way a stale feed is not.
 */
let inFlight: Promise<AwardsData> | null = null;

export function loadAwardsData(): Promise<AwardsData> {
  if (!inFlight) inFlight = getAllWorkoutsWithUsers();
  return inFlight;
}

/** Drops the cache so the next read reflects a lift that was just logged. */
export function invalidateAwardsData(): void {
  inFlight = null;
}

export interface AwardsDataState {
  data: AwardsData | null;
  isLoading: boolean;
  error: boolean;
}

/**
 * Loads the award data, optionally deferred.
 *
 * @param enabled Pass false to hold off - the leaderboard only needs this once
 *   somebody actually taps the trophy, and the normal board should not wait on
 *   a read it may never use.
 */
export function useAwardsData(enabled = true): AwardsDataState {
  // Null means "not settled yet". Loading is derived from that rather than
  // stored, so the effect never has to set state just to say it started.
  const [result, setResult] = useState<{ data: AwardsData | null; error: boolean } | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    loadAwardsData()
      .then((data) => {
        if (!cancelled) setResult({ data, error: false });
      })
      .catch((err) => {
        console.error('Failed to load award data', err);
        // A failed load must not poison every later attempt.
        invalidateAwardsData();
        if (!cancelled) setResult({ data: null, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return {
    data: result?.data ?? null,
    isLoading: enabled && result === null,
    error: result?.error ?? false,
  };
}
